import {
  type FnName,
  isExactNum,
  isIntegerNum,
  isNumEqual,
  type Node,
  type NumNode,
  numValue,
} from "@/lib/tool-kit/calculus/ast";

/**
 * Expression trees back to text, in three dialects that share one layout engine:
 *
 * - `plain`   ASCII that `parseExpression` reads back: `2x cos(x^2)`, `sqrt(x)`, `1/(2x)`.
 * - `unicode` the same with − π √ and superscript digits: `2x cos(x²)`, `sin²(x)`.
 * - `latex`   `2x\cos\left(x^{2}\right)`, `\frac{1}{2\sqrt{x}}`.
 *
 * Sums print as `a − b` rather than `a + (−1)b`; a product of a fraction prints as a
 * fraction; `x^(1/2)` prints as a square root. A number the user typed with a decimal
 * point prints as a decimal when it has a short decimal expansion (`decimals: true`).
 */

export type PrintStyle = "plain" | "unicode" | "latex";

export interface PrintOptions {
  style?: PrintStyle;
  /** Prefer 0.5 to 1/2 when the value has a short terminating decimal expansion. */
  decimals?: boolean;
}

/** Binding strength: a child with a lower level than its context gets brackets. */
const SUM = 1;
const PRODUCT = 2;
const POWER = 3;
const ATOM = 4;

interface Printed {
  text: string;
  level: number;
}

interface Dialect {
  style: PrintStyle;
  decimals: boolean;
}

const SUPERSCRIPTS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const TRIG: ReadonlySet<FnName> = new Set([
  "sin",
  "cos",
  "tan",
  "sec",
  "csc",
  "cot",
  "sinh",
  "cosh",
  "tanh",
]);
const LATEX_NAMES: Record<FnName, string> = {
  sin: "\\sin",
  cos: "\\cos",
  tan: "\\tan",
  sec: "\\sec",
  csc: "\\csc",
  cot: "\\cot",
  asin: "\\arcsin",
  acos: "\\arccos",
  atan: "\\arctan",
  sinh: "\\sinh",
  cosh: "\\cosh",
  tanh: "\\tanh",
  ln: "\\ln",
  log: "\\log_{10}",
  abs: "\\left|",
};

const STARTS_WORD = /^[A-Za-zπ√(\\]/;
const PURE_NUMBER = /^[\d.]+$/;
const STARTS_DIGIT = /^\d/;
const SIMPLE_EXPONENT = /^(?:\d+|[A-Za-z]|pi|π)$/;
const TRAILING_ZEROS = /\.?0+$/;

function minusSign(d: Dialect): string {
  return d.style === "unicode" ? "−" : "-";
}

function paren(text: string): string {
  return `(${text})`;
}

function wrap(printed: Printed, minimum: number): string {
  return printed.level < minimum ? paren(printed.text) : printed.text;
}

/* -------------------------------------------------------------- numbers */

function formatFloat(value: number): string {
  if (Number.isInteger(value) && Math.abs(value) < 1e15) {
    return String(value);
  }
  const text = Number(value.toPrecision(12)).toString();
  return text.includes("e")
    ? value.toPrecision(10).replace(TRAILING_ZEROS, "")
    : text;
}

/** A terminating decimal for n/d, or null if it is long or never ends. */
function terminatingDecimal(n: number, d: number): string | null {
  let rest = d;
  let twos = 0;
  let fives = 0;
  while (rest % 2 === 0) {
    rest /= 2;
    twos += 1;
  }
  while (rest % 5 === 0) {
    rest /= 5;
    fives += 1;
  }
  const places = Math.max(twos, fives);
  if (rest !== 1 || places > 6) {
    return null;
  }
  return Number((n / d).toFixed(places)).toString();
}

function absNumberText(n: number, d: number, dialect: Dialect): Printed {
  if (d === 1) {
    return { text: formatFloat(n), level: ATOM };
  }
  if (dialect.decimals) {
    const decimal = terminatingDecimal(n, d);
    if (decimal !== null) {
      return { text: decimal, level: ATOM };
    }
  }
  if (dialect.style === "latex") {
    return { text: `\\frac{${n}}{${d}}`, level: ATOM };
  }
  return { text: `${n}/${d}`, level: PRODUCT };
}

function numberPrinted(node: NumNode, dialect: Dialect): Printed {
  const value = numValue(node);
  const body = absNumberText(Math.abs(node.n), node.d, dialect);
  if (value < 0) {
    return {
      text: `${minusSign(dialect)}${body.text}`,
      level: SUM,
    };
  }
  return body;
}

/* -------------------------------------------------------------- products */

interface MulParts {
  negative: boolean;
  numerator: Node[];
  denominator: Node[];
  /** Coefficient magnitude as numerator and denominator (denominator 1 for a float). */
  coeffN: number;
  coeffD: number;
}

function negatedExponent(factor: Extract<Node, { kind: "pow" }>): Node {
  const exp = factor.exp as NumNode;
  if (exp.n === -exp.d) {
    return factor.base;
  }
  return {
    kind: "pow",
    base: factor.base,
    exp: { kind: "num", n: -exp.n, d: exp.d },
  };
}

function decompose(factors: Node[], dialect: Dialect): MulParts {
  let n = 1;
  let d = 1;
  let float = 1;
  let exact = true;
  const numerator: Node[] = [];
  const denominator: Node[] = [];
  for (const factor of factors) {
    if (factor.kind === "num") {
      if (isExactNum(factor)) {
        n *= factor.n;
        d *= factor.d;
      } else {
        exact = false;
        float *= numValue(factor);
      }
    } else if (
      factor.kind === "pow" &&
      factor.exp.kind === "num" &&
      factor.exp.n < 0
    ) {
      denominator.push(negatedExponent(factor));
    } else {
      numerator.push(factor);
    }
  }
  let value = exact ? n / d : float * (n / d);
  const negative = value < 0;
  value = Math.abs(value);
  if (!exact) {
    return { negative, numerator, denominator, coeffN: value, coeffD: 1 };
  }
  const nn = Math.abs(n);
  if (dialect.decimals && d !== 1 && terminatingDecimal(nn, d) !== null) {
    return { negative, numerator, denominator, coeffN: value, coeffD: 1 };
  }
  return { negative, numerator, denominator, coeffN: nn, coeffD: d };
}

function joinProduct(parts: Printed[], dialect: Dialect): string {
  let out = "";
  for (const part of parts) {
    if (out === "") {
      out = part.text;
      continue;
    }
    out += separator(out, part.text, dialect) + part.text;
  }
  return out;
}

function separator(left: string, right: string, dialect: Dialect): string {
  if (STARTS_DIGIT.test(right)) {
    if (dialect.style === "latex") {
      return " \\cdot ";
    }
    return dialect.style === "unicode" ? "·" : "*";
  }
  if (dialect.style === "latex") {
    return PURE_NUMBER.test(left) || left.endsWith(")") ? "" : " ";
  }
  if (PURE_NUMBER.test(left) && STARTS_WORD.test(right)) {
    return "";
  }
  return left.endsWith(")") && right.startsWith("(") ? "" : " ";
}

function coefficientPrinted(value: number, dialect: Dialect): Printed {
  return absNumberText(value, 1, dialect);
}

interface Side {
  text: string;
  level: number;
  /** How many factors were joined. */
  count: number;
}

function printSide(
  factors: Node[],
  coefficient: number,
  dialect: Dialect
): Side {
  const parts: Printed[] = [];
  if (coefficient !== 1 || factors.length === 0) {
    parts.push(coefficientPrinted(coefficient, dialect));
  }
  for (const factor of factors) {
    parts.push(print(factor, dialect));
  }
  if (parts.length === 1) {
    return { text: parts[0].text, level: parts[0].level, count: 1 };
  }
  const wrapped = parts.map((part) => ({
    text: wrap(part, PRODUCT),
    level: part.level,
  }));
  return {
    text: joinProduct(wrapped, dialect),
    level: PRODUCT,
    count: parts.length,
  };
}

/** A product as a sign and an unsigned body, so a sum can print "a − b". */
function printMul(
  factors: Node[],
  dialect: Dialect
): { negative: boolean; body: Printed } {
  const parts = decompose(factors, dialect);
  const top = printSide(parts.numerator, parts.coeffN, dialect);
  if (parts.denominator.length === 0 && parts.coeffD === 1) {
    return { negative: parts.negative, body: top };
  }
  const bottom = printSide(parts.denominator, parts.coeffD, dialect);
  if (dialect.style === "latex") {
    return {
      negative: parts.negative,
      body: { text: `\\frac{${top.text}}{${bottom.text}}`, level: ATOM },
    };
  }
  const topText = top.level < PRODUCT ? paren(top.text) : top.text;
  const bottomText =
    bottom.count > 1 || bottom.level < POWER ? paren(bottom.text) : bottom.text;
  return {
    negative: parts.negative,
    body: { text: `${topText}/${bottomText}`, level: PRODUCT },
  };
}

/* ---------------------------------------------------------------- powers */

function exponentText(exp: Node, dialect: Dialect): string {
  const printed = print(exp, dialect);
  if (dialect.style === "latex") {
    return `{${printed.text}}`;
  }
  return SIMPLE_EXPONENT.test(printed.text)
    ? printed.text
    : paren(printed.text);
}

function superscript(exp: Node): string | null {
  if (isIntegerNum(exp) && exp.n >= 2 && exp.n <= 9) {
    return SUPERSCRIPTS[exp.n];
  }
  return null;
}

function squareRoot(base: Node, dialect: Dialect): Printed {
  const inner = print(base, dialect);
  if (dialect.style === "latex") {
    return { text: `\\sqrt{${inner.text}}`, level: ATOM };
  }
  if (dialect.style === "unicode") {
    const simple =
      base.kind === "var" || (base.kind === "num" && inner.level === ATOM);
    return {
      text: simple ? `√${inner.text}` : `√${paren(inner.text)}`,
      level: ATOM,
    };
  }
  return { text: `sqrt(${inner.text})`, level: ATOM };
}

function printPow(
  node: Extract<Node, { kind: "pow" }>,
  dialect: Dialect
): Printed {
  const { base, exp } = node;
  if (exp.kind === "num" && exp.n < 0) {
    return asFraction(node, dialect);
  }
  if (isNumEqual(exp, 0.5)) {
    return squareRoot(base, dialect);
  }
  if (
    dialect.style === "unicode" &&
    base.kind === "call" &&
    TRIG.has(base.fn) &&
    superscript(exp)
  ) {
    return {
      text: `${base.fn}${superscript(exp)}${paren(print(base.arg, dialect).text)}`,
      level: ATOM,
    };
  }
  if (dialect.style === "latex" && base.kind === "call" && TRIG.has(base.fn)) {
    return {
      text: `${LATEX_NAMES[base.fn]}^{${print(exp, dialect).text}}(${print(base.arg, dialect).text})`,
      level: ATOM,
    };
  }
  const baseText = wrap(print(base, dialect), ATOM);
  const sup = dialect.style === "unicode" ? superscript(exp) : null;
  if (sup) {
    return { text: `${baseText}${sup}`, level: POWER };
  }
  return { text: `${baseText}^${exponentText(exp, dialect)}`, level: POWER };
}

function asFraction(node: Node, dialect: Dialect): Printed {
  return printMul([node], dialect).body;
}

/* ----------------------------------------------------------------- calls */

function printCall(
  node: Extract<Node, { kind: "call" }>,
  dialect: Dialect
): Printed {
  const arg = print(node.arg, dialect).text;
  if (node.fn === "abs") {
    if (dialect.style === "latex") {
      return { text: `\\left|${arg}\\right|`, level: ATOM };
    }
    return {
      text: dialect.style === "unicode" ? `|${arg}|` : `abs(${arg})`,
      level: ATOM,
    };
  }
  if (dialect.style === "latex") {
    return {
      text: `${LATEX_NAMES[node.fn]}\\left(${arg}\\right)`,
      level: ATOM,
    };
  }
  return { text: `${node.fn}(${arg})`, level: ATOM };
}

/* ------------------------------------------------------------------ sums */

function printSum(terms: Node[], dialect: Dialect): Printed {
  let out = "";
  terms.forEach((term, index) => {
    const parts = signedParts(term, dialect);
    if (index === 0) {
      out = parts.negative ? `${minusSign(dialect)}${parts.text}` : parts.text;
      return;
    }
    const sign = parts.negative ? ` ${minusSign(dialect)} ` : " + ";
    out += sign + parts.text;
  });
  return { text: out, level: SUM };
}

function signedParts(
  term: Node,
  dialect: Dialect
): { negative: boolean; text: string } {
  if (term.kind === "num") {
    const body = absNumberText(Math.abs(term.n), term.d, dialect);
    return { negative: numValue(term) < 0, text: body.text };
  }
  if (term.kind === "mul") {
    const { negative, body } = printMul(term.factors, dialect);
    return { negative, text: wrap(body, PRODUCT) };
  }
  return { negative: false, text: wrap(print(term, dialect), PRODUCT) };
}

/* ------------------------------------------------------------------ main */

function print(node: Node, dialect: Dialect): Printed {
  switch (node.kind) {
    case "num":
      return numberPrinted(node, dialect);
    case "const":
      if (node.name === "pi") {
        return {
          text: { plain: "pi", unicode: "π", latex: "\\pi" }[dialect.style],
          level: ATOM,
        };
      }
      return { text: "e", level: ATOM };
    case "var":
      return { text: node.name, level: ATOM };
    case "add":
      return printSum(node.terms, dialect);
    case "mul": {
      const { negative, body } = printMul(node.factors, dialect);
      return negative
        ? { text: `${minusSign(dialect)}${wrap(body, PRODUCT)}`, level: SUM }
        : body;
    }
    case "pow":
      return printPow(node, dialect);
    default:
      return printCall(node, dialect);
  }
}

function render(node: Node, options: PrintOptions, style: PrintStyle): string {
  return print(node, { style, decimals: options.decimals ?? false }).text;
}

/** ASCII that `parseExpression` reads back. */
export function toText(node: Node, options: PrintOptions = {}): string {
  return render(
    node,
    options,
    options.style === "unicode" ? "unicode" : "plain"
  );
}

export function toLatex(node: Node, options: PrintOptions = {}): string {
  return render(node, options, "latex");
}
